import type { ReactNode } from 'react';
import { Images } from '@assets';

interface AuthSplitLayoutProps { children: ReactNode; }

/**
 * AuthSplitLayout — two-column split layout.
 * Left: cinematic gradient + dot grid + centered illustration/branding.
 * Right: white panel with logo at top and page card centered.
 */
function AuthSplitLayout({ children }: AuthSplitLayoutProps) {
    return (
        <div className="flex min-h-screen">

            {/* ── Left Panel ───────────────────────────────────────── */}
            <div className="relative hidden lg:flex lg:w-1/2 bg-cinematic flex-col justify-between p-12 text-white overflow-hidden">
                {/* Dot Grid Overlay */}
                <div className="absolute inset-0 dot-grid pointer-events-none opacity-30 z-0" />

                {/* Logo / Header */}
                <div className="relative z-10 flex items-center gap-3">
                    <img
                        src={Images.tpaLogo}
                        alt="TPA Logo"
                        className="h-10 w-auto object-contain"
                    />
                    <span className="h-6 w-px bg-white/20" />
                    <span className="text-[13px] font-extrabold tracking-[0.18em] text-white uppercase">
                        The Purpose Academy
                    </span>
                </div>

                {/* Middle Content */}
                <div className="relative z-10 max-w-md my-auto">
                    <h2 className="text-4xl font-extrabold leading-tight text-white">
                        AI Impact <br />
                        <span className="bg-gradient-to-r from-[oklch(0.55_0.24_295)] via-[oklch(0.62_0.27_0)] to-[oklch(0.72_0.22_35)] bg-clip-text text-transparent">Fellowship</span>
                    </h2>
                    <p className="mt-4 text-base text-slate-300 leading-relaxed">
                        An intensive project cohort where students build real-world AI solutions, work with elite mentors, and create global impact.
                    </p>
                </div>

                {/* Footer */}
                <div className="relative z-10 text-xs text-white/50">
                    © 2026 The Purpose Academy. All rights reserved.
                </div>
            </div>

            {/* ── Right Panel ──────────────────────────────────────── */}
            <div className="flex flex-1 flex-col bg-white">

                {/* Logo */}
                <div className="flex items-center justify-center gap-3 pt-[60px] 2xl:pt-[100px] shrink-0">
                    <img
                        src={Images.tpaLogo}
                        alt="The Purpose Academy"
                        fetchPriority="high"
                        decoding="async"
                        className="h-[40px] 2xl:h-[50px] w-auto select-none object-contain"
                    />
                    <span className="h-8 w-px bg-slate-300" />
                    <span className="text-[14px] font-extrabold tracking-[0.18em] text-navy uppercase">
                        AI IMPACT <span className="bg-gradient-to-r from-[oklch(0.55_0.24_295)] via-[oklch(0.62_0.27_0)] to-[oklch(0.72_0.22_35)] bg-clip-text text-transparent">FELLOWSHIP</span>
                    </span>
                </div>

                {/* Card area — takes remaining height */}
                <div className="flex flex-1 items-center justify-center flex-col p-4 md:p-6 2xl:p-8">
                    {children}
                </div>

            </div>
        </div>
    );
}

export default AuthSplitLayout;
